import type { AddonInstallation, Catalog, Meta } from '../../contracts/src/index.ts';
import { addonCatalogFields, addonExtraAllowed } from '../../contracts/src/addons.ts';
import { SlcError } from './types.ts';
import { utcGuideDay } from '../../contracts/src/live.ts';

export type CatalogChoice = { key: string; addonId: string; addonName: string; catalog: Catalog; guide?: true };
export function catalogChoices(addons: AddonInstallation[]): CatalogChoice[] {
  const seen = new Set<string>();
  return [...addons].sort((a, b) => a.position - b.position).filter(a => a.enabled && !a.manifest.behaviorHints?.configurationRequired)
    .flatMap(a => a.manifest.catalogs.flatMap(catalog => {
      const key = JSON.stringify([a.id, catalog.type, catalog.id]);
      if (seen.has(key)) return []; seen.add(key);
      return [{ key, addonId: a.id, addonName: a.manifest.name, catalog,
        ...(catalog.type === 'tv' && a.manifest.behaviorHints?.epgProvider === true && addonCatalogFields(catalog).some(e => e.name === 'date') ? { guide: true as const } : {}) }];
    }));
}
export function catalogExtras(catalog: Catalog): NonNullable<Catalog['extra']> {
  // Modern extra takes precedence, including an explicitly empty array.
  return addonCatalogFields(catalog);
}
export function catalogRequest(catalog: Catalog, values: Record<string, string>, skip = 0) {
  const extras = catalogExtras(catalog), output: Record<string, string> = {};
  if (!Number.isInteger(skip) || skip < 0 || skip > 10000) throw new SlcError('CATALOG_PAGE', 'This catalog page is outside the supported range.');
  if (skip > 0 && !extras.some(e => e.name === 'skip')) throw new SlcError('CATALOG_PAGE', 'This catalog does not offer more pages.');
  for (const e of extras) {
    const value = e.name === 'skip' ? skip || e.isRequired ? String(skip) : '' : (values[e.name] || '').trim();
    if (e.isRequired && !value) throw new SlcError('CATALOG_FILTER', `Choose ${e.name} before opening this catalog.`);
    if (value.length > 2000 || !addonExtraAllowed(e, value)) throw new SlcError('CATALOG_FILTER', `Choose a supported ${e.name} value.`);
    if (e.name === 'date' && value && !utcGuideDay(value)) throw new SlcError('CATALOG_FILTER', 'Choose a real guide date (YYYY-MM-DD, UTC).');
    if (value) output[e.name] = value;
  }
  if (Object.keys(output).length > 12) throw new SlcError('CATALOG_FILTER', 'This catalog requires too many filters.');
  return output;
}
/** A metadata link may select filters and a page, but cannot silently retarget
 * an unsupported filter/catalog or turn a malformed page into page zero. */
export function catalogLinkRequest(catalog: Catalog, extra: Record<string, string>) {
  const fields = catalogExtras(catalog);
  if (Object.keys(extra).length > 12 || Object.entries(extra).some(([key, value]) => !fields.some(f => f.name === key) ||
      typeof value !== 'string' || value.length > 2000 || /[\u0000-\u001f\u007f]/.test(key + value)))
    throw new SlcError('CATALOG_LINK', 'This catalog link contains unsupported filters.');
  if (extra.skip !== undefined && !/^(0|[1-9][0-9]{0,4})$/.test(extra.skip))
    throw new SlcError('CATALOG_LINK', 'This catalog link contains an unsupported page.');
  const offset = Number(extra.skip || 0), values = { ...extra };
  delete values.skip;
  catalogRequest(catalog, values, offset);
  return { values, offset };
}
export function searchableCatalogs(choices: CatalogChoice[]) {
  return choices.filter(c => {
    const extras = catalogExtras(c.catalog);
    return extras.some(e => e.name === 'search') && extras.every(e => !e.isRequired || ['search', 'skip'].includes(e.name));
  });
}
export type SearchRow = { choice: CatalogChoice; items: Meta[]; hasMore: boolean; error?: string };
/** One user-requested batch, at most six catalogs/three concurrent requests.
 * Keep six previews per catalog, not every returned metadata object. */
export async function searchCatalogBatch(choices: CatalogChoice[], query: string, offset: number, signal: AbortSignal,
  fetch: (choice: CatalogChoice, extras: Record<string, string>, signal: AbortSignal) => Promise<{ items: Meta[]; received: number }>) {
  query = query.trim();
  if (!query || query.length > 300 || !Number.isInteger(offset) || offset < 0 || offset % 6 !== 0) throw new SlcError('SEARCH_QUERY', 'Enter a title to search for.');
  const eligible = searchableCatalogs(choices), batch = eligible.slice(offset, offset + 6), rows: SearchRow[] = [];
  const current = () => { if (signal.aborted) throw new SlcError('REQUEST_CANCELLED', 'This search was cancelled.'); };
  let cursor = 0;
  await Promise.all(Array.from({ length: Math.min(3, batch.length) }, async () => {
    while (cursor < batch.length) {
      current(); const index = cursor++, choice = batch[index];
      try {
        const { items, received } = await fetch(choice, catalogRequest(choice.catalog, { search: query }), signal); current();
        rows[index] = { choice, items: items.slice(0, 6), hasMore: items.length > 6 || received > 0 && catalogExtras(choice.catalog).some(e => e.name === 'skip') };
      } catch (e) { current(); rows[index] = { choice, items: [], hasMore: false, error: e instanceof Error ? e.message : 'This catalog could not respond.' }; }
    }
  }));
  current();
  return { rows, total: eligible.length, nextOffset: offset + batch.length < eligible.length ? offset + 6 : null };
}
